import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { camelCaseToCapital } from '../../../../utils/topicsMapping';
import { camelCaseToCapitalIndustries } from '../../../../utils/industriesMapping';
import { popupActions } from '../../../../redux/slices/onboardingPopupsSlice';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

const cardSx = {
  background: '#1c1c1c',
  border: '1px solid #444444',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

const headerSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  padding: '15px 25px',
  borderBottom: '1px solid #444444',
  // Phone: a bare icon (no edit action) lines up with the 40px tappable ones.
  [MOBILE_SX]: {
    display: 'flex',
    minHeight: 56,
    padding: '8px 8px 8px 16px',
    '& > .profileSettingsIcon': { mr: '11px' }
  }
};

const bodySx = {
  padding: '15px 25px',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '20px',
  [MOBILE_SX]: { padding: '12px 16px 16px', gap: '12px' }
};

/** The edit icon's tap area: 40px on a phone around the same 18px icon. */
const actionSx = { cursor: 'pointer', [MOBILE_SX]: { p: '11px', display: 'flex' } };

const outlineIcon = (
  <Box
    className="profileSettingsIcon"
    sx={{ width: 18, height: 18, p: '3px', position: 'relative', overflow: 'hidden', display: 'flex' }}
  >
    <Box
      sx={{
        width: 13.5,
        height: 13.3,
        position: 'absolute',
        left: '2.25px',
        top: '2.35px',
        outline: '2px solid #FDFDFD',
        outlineOffset: '-1px'
      }}
    />
  </Box>
);

const chipStyles = {
  px: 1,
  py: 0.5,
  borderRadius: '20px',
  outline: '1px solid #444444',
  outlineOffset: '-1px',
  color: '#FDFDFD',
  fontSize: '12px',
  fontWeight: 400,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const ProfileSettings = () => {
  const userDetails = useAppSelector((state) => state.userProfile);
  const popups = useAppSelector((state) => state.onboardingPopups.data);
  const dispatch = useAppDispatch();
  const personal = userDetails.data?.personal_details;

  const rawName = [personal?.firstName, personal?.lastName].filter(Boolean).join(' ');
  const fullName = rawName || 'Not set';
  const email = personal?.email || 'Not set';
  const roleLabel = personal?.category;
  const initial = useMemo(() => {
    const letters = rawName
      .trim()
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean);
    return letters[0]?.toUpperCase() || 'U';
  }, [rawName]);

  const topicsIndustries = useMemo(() => {
    if (!userDetails.data) return [];
    return [
      ...camelCaseToCapitalIndustries(userDetails.data.selectedIndustries),
      ...(camelCaseToCapital(userDetails.data.selectedTopics) || [])
    ].filter(Boolean);
  }, [userDetails.data]);

  const hobbies = personal?.hobbies || [];
  const disabilities = personal?.disability || [];
  const socialHandles = personal?.social;

  const openProfile = () =>
    dispatch(
      popupActions.updatePopups({
        profile: { ...popups.profile, open: true }
      })
    );

  const openDigitalIntro = () =>
    dispatch(
      popupActions.updatePopups({
        digitalIntro: true
      })
    );

  const openWorkHistory = () =>
    dispatch(
      popupActions.updatePopups({
        workHistory: { ...popups.workHistory, open: true, action: 'Add' }
      })
    );

  const openEducation = () =>
    dispatch(
      popupActions.updatePopups({
        education: { ...popups.education, open: true, action: 'Add' }
      })
    );

  const openHobbies = () =>
    dispatch(
      popupActions.updatePopups({
        hobbies: true
      })
    );

  const openSocial = () =>
    dispatch(
      popupActions.updatePopups({
        social: true
      })
    );

  return (
    <Box className="settingsStack" sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: '25px',
          width: '100%',
          [MOBILE_SX]: { gridTemplateColumns: 'minmax(0, 1fr)', gap: '12px' }
        }}
      >
        {/* Personal Information */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Personal information</Typography>
            </Box>
            <Box sx={actionSx} onClick={openProfile}>
              {outlineIcon}
            </Box>
          </Box>
          <Box sx={bodySx}>
            <Box sx={{ display: 'inline-flex', gap: '15px', alignItems: 'center', [MOBILE_SX]: { gap: '12px' } }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  background: '#1A55A8',
                  borderRadius: '5px',
                  outline: '1px solid #FDFDFD',
                  outlineOffset: '-1px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FDFDFD',
                  fontSize: 36,
                  fontWeight: 400,
                  [MOBILE_SX]: { width: 56, height: 56, flexShrink: 0, fontSize: 24 }
                }}
              >
                {initial}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  pt: 1,
                  pb: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  // Phone: a long email wraps inside the card instead of running off it.
                  [MOBILE_SX]: { minWidth: 0, gap: '8px', overflowWrap: 'anywhere' }
                }}
              >
                <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, lineHeight: '19.8px' }}>
                  {fullName}
                </Typography>
                <Typography sx={{ color: '#868686', fontSize: 16, fontWeight: 500, lineHeight: '17.6px' }}>
                  {email}
                </Typography>
                {roleLabel ? (
                  <Box sx={{ display: 'inline-flex', gap: '6px' }}>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: '20px',
                        outline: '1px solid #02A9FE',
                        outlineOffset: '-1px',
                        color: '#FDFDFD',
                        fontSize: '12px',
                        fontWeight: 400,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {roleLabel}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Bio */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Bio</Typography>
            </Box>
            <Box sx={actionSx} onClick={openDigitalIntro}>
              {outlineIcon}
            </Box>
          </Box>
          <Box sx={bodySx}>
            <Typography sx={{ color: '#FDFDFD', fontSize: 14, lineHeight: '19.6px' }}>
              {personal?.bio || 'Not added yet'}
            </Typography>
          </Box>
        </Box>

        {/* My Attributes */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>My attributes</Typography>
            </Box>
            {outlineIcon}
          </Box>
          <Box sx={bodySx}>
            {roleLabel ? (
              <Box sx={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
                <Box sx={chipStyles}>{roleLabel}</Box>
              </Box>
            ) : (
              <Typography sx={{ color: '#868686', fontSize: 14 }}>Not added yet</Typography>
            )}
          </Box>
        </Box>

        {/* Topics & Industries */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Topics and industries</Typography>
            </Box>
            {outlineIcon}
          </Box>
          <Box sx={bodySx}>
            {topicsIndustries.length ? (
              <Box sx={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
                {topicsIndustries.map((chip) => (
                  <Box key={chip} sx={chipStyles}>
                    {chip}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: '#868686', fontSize: 14 }}>Not added yet</Typography>
            )}
          </Box>
        </Box>

        {/* Work Experience */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Work experience</Typography>
            </Box>
            <Box sx={actionSx} onClick={openWorkHistory}>
              {outlineIcon}
            </Box>
          </Box>
          <Box sx={bodySx}>
            <Typography sx={{ color: '#868686', fontSize: 14 }}>Not added yet</Typography>
          </Box>
        </Box>

        {/* Education & Skills */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Education and skills</Typography>
            </Box>
            <Box sx={actionSx} onClick={openEducation}>
              {outlineIcon}
            </Box>
          </Box>
          <Box sx={bodySx}>
            <Typography sx={{ color: '#868686', fontSize: 14 }}>Not added yet</Typography>
          </Box>
        </Box>

        {/* Video Introduction */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Video introduction</Typography>
            </Box>
            <Box sx={actionSx} onClick={openDigitalIntro}>
              {outlineIcon}
            </Box>
          </Box>
          <Box sx={bodySx}>
            <Typography sx={{ color: '#868686', fontSize: 14 }}>Not added yet</Typography>
          </Box>
        </Box>

        {/* Social Handles */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Social handles</Typography>
            </Box>
            <Box sx={actionSx} onClick={openSocial}>
              {outlineIcon}
            </Box>
          </Box>
          <Box sx={bodySx}>
            <Box sx={{ display: 'inline-flex', gap: '15px', alignItems: 'center' }}>
              {socialHandles
                ? Object.values(socialHandles)
                    .filter(Boolean)
                    .map((item, index) => (
                      <Box
                        key={`${item}-${index}`}
                        component="span"
                        sx={{ width: 20, height: 20, borderRadius: '3px', background: '#2A2A2A' }}
                      />
                    ))
                : [1, 2, 3, 4, 5].map((item) => (
                <Box
                  key={item}
                  component="span"
                  sx={{ width: 20, height: 20, borderRadius: '3px', background: '#2A2A2A' }}
                />
                ))}
            </Box>
          </Box>
        </Box>

        {/* Disability */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Disability</Typography>
            </Box>
            {outlineIcon}
          </Box>
          <Box sx={bodySx}>
            {disabilities.length ? (
              <Box sx={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
                {disabilities.map((label) => (
                  <Box key={label} sx={chipStyles}>
                    {label}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: '#FDFDFD', fontSize: 14 }}>No Disabilities</Typography>
            )}
          </Box>
        </Box>

        {/* Hobbies & Interests */}
        <Box sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Hobbies and interests</Typography>
            </Box>
            <Box sx={actionSx} onClick={openHobbies}>
              {outlineIcon}
            </Box>
          </Box>
          <Box sx={bodySx}>
            {hobbies.length ? (
              <Box sx={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
                {hobbies.map((hobby) => (
                  <Box key={hobby} sx={chipStyles}>
                    {hobby}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: '#868686', fontSize: 14 }}>Not added yet</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileSettings;
